import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Button,
  TextField,
  Grid,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Divider,
  Avatar,
} from '@mui/material';
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Refresh,
  Add,
  Cancel,
  Undo,
  TrendingUp,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { toast } from 'react-toastify';
import InterestRateService from '../../services/interestRateService';
import UserSpecificInterestRateService from '../../services/userSpecificInterestRateService';
import UserService from '../../services/userService';
import useRoleColors from '../../hooks/useRoleColors';
import { Tabs, Tab, Autocomplete } from '@mui/material';

const InterestRateManagement = ({ user }) => {
  const theme = useTheme();
  const { softBg, accentGradient } = useRoleColors();
  const [rates, setRates] = useState([]);
  const [userSpecificRates, setUserSpecificRates] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(0); // 0: General, 1: User-Specific
  const [addDialog, setAddDialog] = useState(false);
  const [deactivateDialog, setDeactivateDialog] = useState({ open: false, rateId: null });
  const [activateDialog, setActivateDialog] = useState({ open: false, rateId: null });
  const [formData, setFormData] = useState({
    // Cash Credit Rates
    nakdiRate: '',
    nakdiCommissionRate: '',
    // Gayrinakdi Kredi Oranları
    gayrinakdiCommissionRate: '',
    // Ortak alanlar
    expirationDate: new Date(),
    description: '',
  });
  const [userSpecificFormData, setUserSpecificFormData] = useState({
    userId: null,
    nakdiRate: '',
    nakdiCommissionRate: '',
    gayrinakdiCommissionRate: '',
    expirationDate: new Date(),
    description: '',
  });

  // Fetch rates
  const fetchRates = async () => {
    setLoading(true);
    setError('');

    const result = await InterestRateService.getAllRates();
    
    if (result.success) {
      setRates(result.data || []);
    } else {
      setError(result.message);
    }
    
    setLoading(false);
  };

  // Fetch user-specific rates
  const fetchUserSpecificRates = async () => {
    setLoading(true);
    setError('');

    const result = await UserSpecificInterestRateService.getAllRates();
    
    if (result.success) {
      setUserSpecificRates(result.data || []);
    } else {
      setError(result.message);
    }
    
    setLoading(false);
  };

  // Fetch users for dropdown (exclude admin users, only active users)
  const fetchUsers = async () => {
    try {
      const result = await UserService.getAllUsers(1, 1000);
      if (result.success) {
        // Filter out admin users and inactive users
        const filteredUsers = (result.data || []).filter(u => {
          // Exclude admin users (check if user has Admin role)
          const isAdmin = u.role === 'Admin' || (u.roles && Array.isArray(u.roles) && u.roles.some(r => r === 'Admin' || (typeof r === 'object' && r.name === 'Admin')));
          // Only include active, non-deleted users
          // UserStatus enum: Active = 1, Inactive = 0
          const isActive = u.status === 1 || u.status === 'Active';
          return !isAdmin && isActive && !u.isDeleted;
        });
        
        console.log('Total users fetched:', result.data?.length || 0);
        console.log('Filtered users (non-admin, active):', filteredUsers.length);
        console.log('Sample user data:', result.data?.[0]);
        setUsers(filteredUsers);
      } else {
        console.error('Failed to fetch users:', result.message);
        setError(result.message || 'Failed to fetch users');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Error fetching users: ' + error.message);
    }
  };

  useEffect(() => {
    fetchRates();
    fetchUsers();
  }, []);

  useEffect(() => {
    if (activeTab === 1) {
      fetchUserSpecificRates();
    }
  }, [activeTab]);

  // Handle add rate
  const handleAddRate = async () => {
    try {
      // ExpirationDate nullable olabilir (sınırsız)
      const formattedExpirationDate = formData.expirationDate instanceof Date 
        ? formData.expirationDate.toISOString().split('T')[0]
        : (formData.expirationDate || null);

      // Nakdi validasyonu: Faiz ve komisyon birlikte girilmeli
      if (formData.nakdiRate && parseFloat(formData.nakdiRate) > 0) {
        if (!formData.nakdiCommissionRate || parseFloat(formData.nakdiCommissionRate) <= 0) {
          toast.error('Please enter commission rate for cash credit ');
          return;
        }
      }

      // At least one rate must be entered
      if (!formData.nakdiRate && !formData.gayrinakdiCommissionRate) {
        toast.error('Please enter at least one credit rate (Interest rate for Cash or Commission rate for Non-Cash)');
        return;
      }

      // Create a single record - all values together
      const rateData = {
        rate: formData.nakdiRate && parseFloat(formData.nakdiRate) > 0 
          ? parseFloat(formData.nakdiRate) 
          : null,
        nakdiCommissionRate: formData.nakdiCommissionRate && parseFloat(formData.nakdiCommissionRate) > 0
          ? parseFloat(formData.nakdiCommissionRate)
          : null,
        gayrinakdiCommissionRate: formData.gayrinakdiCommissionRate && parseFloat(formData.gayrinakdiCommissionRate) > 0
          ? parseFloat(formData.gayrinakdiCommissionRate)
          : null,
        effectiveDate: new Date().toISOString().split('T')[0], // Today's date
        expirationDate: formattedExpirationDate,
        type: 1, // Default to Cash (no longer important since it's a single record)
        description: formData.description || '',
      };

      const result = await InterestRateService.createRate(rateData);
      if (!result.success) {
        toast.error('Failed to create interest rate: ' + result.message);
        return;
      }

      setAddDialog(false);
      setFormData({
        nakdiRate: '',
        nakdiCommissionRate: '',
        gayrinakdiCommissionRate: '',
        expirationDate: new Date(),
        description: '',
      });
      fetchRates();
    } catch (error) {
      console.error('Error in handleAddRate:', error);
      toast.error('An error occurred: ' + error.message);
    }
  };

  // Handle deactivate rate
  const handleDeactivateRate = async () => {
    if (!deactivateDialog.rateId) return;
    
    const result = await InterestRateService.deactivateRate(deactivateDialog.rateId);
    if (result.success) {
      setDeactivateDialog({ open: false, rateId: null });
      fetchRates();
    }
  };

  // Handle activate rate
  const handleActivateRate = async () => {
    if (!activateDialog.rateId) return;
    
    const result = await InterestRateService.activateRate(activateDialog.rateId);
    if (result.success) {
      setActivateDialog({ open: false, rateId: null });
      fetchRates();
    }
  };

  // Get type text
  const getTypeText = (type) => {
    switch (type) {
      case 1: return 'Cash';
      case 2: return 'Non-Cash';
      default: return 'Unknown';
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US');
  };

  // Get active rate (all values are in the same record since it's a single record)
  const getActiveRate = () => {
    return rates.find(r => r.isActive && 
      (!r.expirationDate || new Date(r.expirationDate) >= new Date()));
  };

  const activeRate = getActiveRate();

  // Handle add user-specific rate
  const handleAddUserSpecificRate = async () => {
    try {
      if (!userSpecificFormData.userId) {
        toast.error('Please select a user');
        return;
      }

      const formattedExpirationDate = userSpecificFormData.expirationDate instanceof Date 
        ? userSpecificFormData.expirationDate.toISOString().split('T')[0]
        : (userSpecificFormData.expirationDate || null);

      if (userSpecificFormData.nakdiRate && parseFloat(userSpecificFormData.nakdiRate) > 0) {
        if (!userSpecificFormData.nakdiCommissionRate || parseFloat(userSpecificFormData.nakdiCommissionRate) <= 0) {
          toast.error('Please enter commission rate for cash credit');
          return;
        }
      }

      // Check if at least one rate is provided
      const hasNakdiRate = userSpecificFormData.nakdiRate && parseFloat(userSpecificFormData.nakdiRate) > 0;
      const hasGayrinakdiRate = userSpecificFormData.gayrinakdiCommissionRate && parseFloat(userSpecificFormData.gayrinakdiCommissionRate) > 0;
      
      if (!hasNakdiRate && !hasGayrinakdiRate) {
        toast.error('Please enter at least one credit rate (Interest rate for Cash or Commission rate for Non-Cash)');
        return;
      }

      const rateData = {
        userId: userSpecificFormData.userId.userId || userSpecificFormData.userId,
        rate: userSpecificFormData.nakdiRate && parseFloat(userSpecificFormData.nakdiRate) > 0 
          ? parseFloat(userSpecificFormData.nakdiRate) 
          : null,
        nakdiCommissionRate: userSpecificFormData.nakdiCommissionRate && parseFloat(userSpecificFormData.nakdiCommissionRate) > 0
          ? parseFloat(userSpecificFormData.nakdiCommissionRate)
          : null,
        gayrinakdiCommissionRate: userSpecificFormData.gayrinakdiCommissionRate && parseFloat(userSpecificFormData.gayrinakdiCommissionRate) > 0
          ? parseFloat(userSpecificFormData.gayrinakdiCommissionRate)
          : null,
        effectiveDate: new Date().toISOString().split('T')[0],
        expirationDate: formattedExpirationDate,
        description: userSpecificFormData.description || '',
      };

      const result = await UserSpecificInterestRateService.createRate(rateData);
      if (!result.success) {
        toast.error('Failed to create user-specific rate: ' + result.message);
        return;
      }

      setAddDialog(false);
      setUserSpecificFormData({
        userId: null,
        nakdiRate: '',
        nakdiCommissionRate: '',
        gayrinakdiCommissionRate: '',
        expirationDate: new Date(),
        description: '',
      });
      fetchUserSpecificRates();
    } catch (error) {
      console.error('Error in handleAddUserSpecificRate:', error);
      toast.error('An error occurred: ' + error.message);
    }
  };

  // Handle deactivate user-specific rate
  const handleDeactivateUserSpecificRate = async () => {
    if (!deactivateDialog.rateId) return;
    
    const result = await UserSpecificInterestRateService.deactivateRate(deactivateDialog.rateId);
    if (result.success) {
      setDeactivateDialog({ open: false, rateId: null });
      fetchUserSpecificRates();
    }
  };

  // Handle activate user-specific rate
  const handleActivateUserSpecificRate = async () => {
    if (!activateDialog.rateId) return;
    
    const result = await UserSpecificInterestRateService.activateRate(activateDialog.rateId);
    if (result.success) {
      setActivateDialog({ open: false, rateId: null });
      fetchUserSpecificRates();
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', py: 4, px: { xs: 2, sm: 3 } }}>
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        
        {/* Main Card */}
        <Card elevation={24} sx={{ 
          borderRadius: 4,
          overflow: 'hidden'
        }}>
          
          {/* Header Banner */}
          <Box sx={{ 
            background: accentGradient,
            p: 3,
            color: 'white'
          }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item>
                <Avatar sx={{ 
                  width: 64, 
                  height: 64,
                  background: 'rgba(255,255,255,0.25)',
                  border: '3px solid rgba(255,255,255,0.3)'
                }}>
                  <TrendingUp sx={{ fontSize: 32 }} />
                </Avatar>
              </Grid>
              <Grid item xs>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Credit Rates
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Manage interest and commission rates
                </Typography>
              </Grid>
              <Grid item>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={<Refresh />}
                    onClick={fetchRates}
                    disabled={loading}
                    sx={{
                      background: 'rgba(255,255,255,0.2)',
                      color: 'white',
                      '&:hover': {
                        background: 'rgba(255,255,255,0.3)',
                      }
                    }}
                  >
                    Refresh
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => setAddDialog(true)}
                    sx={{
                      background: 'rgba(255,255,255,0.2)',
                      color: 'white',
                      '&:hover': {
                        background: 'rgba(255,255,255,0.3)',
                      }
                    }}
                  >
                    {activeTab === 0 ? 'New General Rate' : 'New User-Specific Rate'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
            <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
              <Tab label="General Rates" />
              <Tab label="User-Specific Rates" />
            </Tabs>
          </Box>

          {/* Content */}
          <CardContent sx={{ p: 4 }}>
            {activeTab === 0 ? (
              <>

            {/* Current Active Rates */}
            <Grid container spacing={2} sx={{ mb: 4 }}>
              <Grid item xs={12} md={6}>
                <Card 
                  variant="outlined" 
                  sx={{ 
                    borderColor: activeRate && activeRate.rate ? 'success.main' : 'grey.300',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 3
                  }}
                >
                  <CardContent sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    flex: 1,
                    p: 3,
                    minHeight: 180
                  }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                      Cash Interest Rate (Yearly)
                    </Typography>
                    {activeRate && activeRate.rate ? (
                      <>
                        <Typography variant="h4" fontWeight="bold" color="primary" sx={{ mb: 1 }}>
                          Interest: %{activeRate.rate}
                        </Typography>
                        {activeRate.nakdiCommissionRate && (
                          <Typography variant="h6" fontWeight="600" color="secondary" sx={{ mb: 2 }}>
                            Commission: %{activeRate.nakdiCommissionRate}
                          </Typography>
                        )}
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 'auto' }}>
                          Expiration: {activeRate.expirationDate ? formatDate(activeRate.expirationDate) : 'Unlimited'}
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 'auto' }}>
                        Cash interest rate not found
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card 
                  variant="outlined" 
                  sx={{ 
                    borderColor: activeRate && activeRate.gayrinakdiCommissionRate ? 'success.main' : 'grey.300',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 3
                  }}
                >
                  <CardContent sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    flex: 1,
                    p: 3,
                    minHeight: 180
                  }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                      Non-Cash Commission Rate (Yearly)
                    </Typography>
                    {activeRate && activeRate.gayrinakdiCommissionRate ? (
                      <>
                        <Typography variant="h4" fontWeight="bold" color="secondary" sx={{ mb: 2 }}>
                          Commission: %{activeRate.gayrinakdiCommissionRate}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 'auto' }}>
                          Expiration: {activeRate.expirationDate ? formatDate(activeRate.expirationDate) : 'Unlimited'}
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 'auto' }}>
                        Non-cash commission rate not found
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Rates Table */}
            <Box sx={{ mt: 4, borderRadius: 3, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ m: 3 }}>{error}</Alert>
          ) : rates.length === 0 ? (
            <Alert severity="info" sx={{ m: 3 }}>No interest rate found</Alert>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Cash Interest Rate (%)</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Cash Commission Rate (%)</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Non-Cash Commission Rate (%)</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Expiration Date</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: 'text.primary', textAlign: 'center' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: 'text.primary', textAlign: 'center' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rates.map((rate) => (
                    <TableRow
                      key={rate.id}
                      hover
                      sx={{
                        '&:hover': {
                          backgroundColor: softBg,
                          borderLeftColor: rate.isActive ? 'success.main' : 'grey.300'
                        },
                        borderLeft: '4px solid transparent'
                      }}
                    >
                      <TableCell sx={{ fontWeight: 600, color: rate.rate ? 'primary.main' : 'text.secondary' }}>
                        {rate.rate ? (
                          <Typography variant="body1" sx={{ fontWeight: 700, color: 'primary.main' }}>
                            %{rate.rate}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: rate.nakdiCommissionRate ? 'secondary.main' : 'text.secondary' }}>
                        {rate.nakdiCommissionRate ? (
                          <Typography variant="body1" sx={{ fontWeight: 700, color: 'secondary.main' }}>
                            %{rate.nakdiCommissionRate}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: rate.gayrinakdiCommissionRate ? 'secondary.main' : 'text.secondary' }}>
                        {rate.gayrinakdiCommissionRate ? (
                          <Typography variant="body1" sx={{ fontWeight: 700, color: 'secondary.main' }}>
                            %{rate.gayrinakdiCommissionRate}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                        {rate.expirationDate ? formatDate(rate.expirationDate) : 'Unlimited'}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Chip
                          label={rate.isActive ? 'Active' : 'Inactive'}
                          size="small"
                          color={rate.isActive ? 'success' : 'default'}
                          sx={{ fontWeight: 600, borderRadius: 3, color: rate.isActive ? 'white' : 'inherit' }}
                        />
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                        {rate.description || '-'}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          {rate.isActive ? (
                            <Tooltip title="Deactivate">
                              <IconButton
                                size="small"
                                onClick={() => setDeactivateDialog({ open: true, rateId: rate.id })}
                                sx={{
                                  color: 'error.main',
                                  backgroundColor: 'error.lighter',
                                  '&:hover': {
                                    backgroundColor: 'error.main',
                                    color: 'white',
                                    transform: 'scale(1.1)'
                                  },
                                  transition: 'all 0.2s'
                                }}
                              >
                                <Cancel fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Tooltip title="Activate">
                              <IconButton
                                size="small"
                                onClick={() => setActivateDialog({ open: true, rateId: rate.id })}
                                sx={{
                                  color: 'warning.main',
                                  backgroundColor: 'warning.lighter',
                                  '&:hover': {
                                    backgroundColor: 'warning.main',
                                    color: 'white',
                                    transform: 'scale(1.1)'
                                  },
                                  transition: 'all 0.2s'
                                }}
                              >
                                <Undo fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
            </Box>
              </>
            ) : (
              <>
                {/* User-Specific Rates Table */}
                <Box sx={{ mt: 4, borderRadius: 3, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
                  {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : error ? (
                    <Alert severity="error" sx={{ m: 3 }}>{error}</Alert>
                  ) : userSpecificRates.length === 0 ? (
                    <Alert severity="info" sx={{ m: 3 }}>No user-specific interest rate found</Alert>
                  ) : (
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow sx={{ backgroundColor: 'grey.50' }}>
                            <TableCell sx={{ fontWeight: 700 }}>User</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Cash Interest Rate (%)</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Cash Commission Rate (%)</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Non-Cash Commission Rate (%)</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Expiration Date</TableCell>
                            <TableCell sx={{ fontWeight: 700, textAlign: 'center' }}>Status</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                            <TableCell sx={{ fontWeight: 700, textAlign: 'center' }}>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {userSpecificRates.map((rate) => (
                            <TableRow
                              key={rate.id}
                              hover
                              sx={{
                                '&:hover': {
                                  backgroundColor: softBg,
                                  borderLeftColor: rate.isActive ? 'success.main' : 'grey.300'
                                },
                                borderLeft: '4px solid transparent'
                              }}
                            >
                              <TableCell>
                                <Typography variant="body2" fontWeight={600}>
                                  {rate.userName || rate.userCompanyName || `User ID: ${rate.userId}`}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                {rate.rate ? (
                                  <Typography variant="body1" sx={{ fontWeight: 700, color: 'primary.main' }}>
                                    %{rate.rate}
                                  </Typography>
                                ) : (
                                  <Typography variant="body2" color="text.secondary">-</Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                {rate.nakdiCommissionRate ? (
                                  <Typography variant="body1" sx={{ fontWeight: 700, color: 'secondary.main' }}>
                                    %{rate.nakdiCommissionRate}
                                  </Typography>
                                ) : (
                                  <Typography variant="body2" color="text.secondary">-</Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                {rate.gayrinakdiCommissionRate ? (
                                  <Typography variant="body1" sx={{ fontWeight: 700, color: 'secondary.main' }}>
                                    %{rate.gayrinakdiCommissionRate}
                                  </Typography>
                                ) : (
                                  <Typography variant="body2" color="text.secondary">-</Typography>
                                )}
                              </TableCell>
                              <TableCell sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                                {rate.expirationDate ? formatDate(rate.expirationDate) : 'Unlimited'}
                              </TableCell>
                              <TableCell sx={{ textAlign: 'center' }}>
                                <Chip
                                  label={rate.isActive ? 'Active' : 'Inactive'}
                                  size="small"
                                  color={rate.isActive ? 'success' : 'default'}
                                  sx={{ fontWeight: 600, borderRadius: 3 }}
                                />
                              </TableCell>
                              <TableCell sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                                {rate.description || '-'}
                              </TableCell>
                              <TableCell sx={{ textAlign: 'center' }}>
                                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                                  {rate.isActive ? (
                                    <Tooltip title="Deactivate">
                                      <IconButton
                                        size="small"
                                        onClick={() => setDeactivateDialog({ open: true, rateId: rate.id })}
                                        sx={{
                                          color: 'error.main',
                                          backgroundColor: 'error.lighter',
                                          '&:hover': {
                                            backgroundColor: 'error.main',
                                            color: 'white',
                                            transform: 'scale(1.1)'
                                          },
                                          transition: 'all 0.2s'
                                        }}
                                      >
                                        <Cancel fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  ) : (
                                    <Tooltip title="Activate">
                                      <IconButton
                                        size="small"
                                        onClick={() => setActivateDialog({ open: true, rateId: rate.id })}
                                        sx={{
                                          color: 'warning.main',
                                          backgroundColor: 'warning.lighter',
                                          '&:hover': {
                                            backgroundColor: 'warning.main',
                                            color: 'white',
                                            transform: 'scale(1.1)'
                                          },
                                          transition: 'all 0.2s'
                                        }}
                                      >
                                        <Undo fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>
              </>
            )}
          </CardContent>
        </Card>

        {/* Add Rate Dialog */}
        <Dialog 
          open={addDialog} 
          onClose={() => {
            setAddDialog(false);
            if (activeTab === 0) {
              setFormData({
                nakdiRate: '',
                nakdiCommissionRate: '',
                gayrinakdiCommissionRate: '',
                expirationDate: new Date(),
                description: '',
              });
            } else {
              setUserSpecificFormData({
                userId: null,
                nakdiRate: '',
                nakdiCommissionRate: '',
                gayrinakdiCommissionRate: '',
                expirationDate: new Date(),
                description: '',
              });
            }
          }} 
          maxWidth="sm" 
          fullWidth
        >
          <DialogTitle>
            {activeTab === 0 ? 'Add New General Rate' : 'Add New User-Specific Rate'}
          </DialogTitle>
          
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
              {activeTab === 1 && (
                <Autocomplete
                  options={users}
                  getOptionLabel={(option) => 
                    option.name && option.surname 
                      ? `${option.name} ${option.surname}${option.companyName ? ` (${option.companyName})` : ''}`
                      : option.companyName || option.email || `User ID: ${option.userId}`
                  }
                  value={userSpecificFormData.userId}
                  onChange={(e, newValue) => setUserSpecificFormData({ ...userSpecificFormData, userId: newValue })}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select User"
                      required
                      fullWidth
                    />
                  )}
                />
              )}
              {/* Cash Credit Rates */}
              <Box>
                <Typography variant="subtitle1" fontWeight="600" color="text.primary" sx={{ mb: 2 }}>
                  Cash Credit Rates
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Interest Rate (Yearly) (%)"
                      type="number"
                      value={activeTab === 0 ? formData.nakdiRate : userSpecificFormData.nakdiRate}
                      onChange={(e) => {
                        if (activeTab === 0) {
                          setFormData({ ...formData, nakdiRate: e.target.value });
                        } else {
                          setUserSpecificFormData({ ...userSpecificFormData, nakdiRate: e.target.value });
                        }
                      }}
                      inputProps={{ step: "0.01", min: "0" }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Commission Rate (%)"
                      type="number"
                      value={activeTab === 0 ? formData.nakdiCommissionRate : userSpecificFormData.nakdiCommissionRate}
                      onChange={(e) => {
                        if (activeTab === 0) {
                          setFormData({ ...formData, nakdiCommissionRate: e.target.value });
                        } else {
                          setUserSpecificFormData({ ...userSpecificFormData, nakdiCommissionRate: e.target.value });
                        }
                      }}
                      inputProps={{ step: "0.01", min: "0" }}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Divider />

              {/* Non-Cash Credit Rates */}
              <Box>
                <Typography variant="subtitle1" fontWeight="600" color="text.primary" sx={{ mb: 2 }}>
                  Non-Cash Credit Rates
                </Typography>
                <TextField
                  fullWidth
                  label="Commission Rate (%)"
                  type="number"
                  value={activeTab === 0 ? formData.gayrinakdiCommissionRate : userSpecificFormData.gayrinakdiCommissionRate}
                  onChange={(e) => {
                    if (activeTab === 0) {
                      setFormData({ ...formData, gayrinakdiCommissionRate: e.target.value });
                    } else {
                      setUserSpecificFormData({ ...userSpecificFormData, gayrinakdiCommissionRate: e.target.value });
                    }
                  }}
                  inputProps={{ step: "0.01", min: "0" }}
                />
              </Box>

              <Divider />

              {/* Common Fields */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DateTimePicker
                    label="Expiration Date"
                    value={activeTab === 0 ? formData.expirationDate : userSpecificFormData.expirationDate}
                    onChange={(newValue) => {
                      if (activeTab === 0) {
                        setFormData({ ...formData, expirationDate: newValue });
                      } else {
                        setUserSpecificFormData({ ...userSpecificFormData, expirationDate: newValue });
                      }
                    }}
                    renderInput={(params) => (
                      <TextField 
                        {...params} 
                        fullWidth 
                      />
                    )}
                  />
                </LocalizationProvider>
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={3}
                  value={activeTab === 0 ? formData.description : userSpecificFormData.description}
                  onChange={(e) => {
                    if (activeTab === 0) {
                      setFormData({ ...formData, description: e.target.value });
                    } else {
                      setUserSpecificFormData({ ...userSpecificFormData, description: e.target.value });
                    }
                  }}
                />
              </Box>
            </Box>
          </DialogContent>
          
          <DialogActions>
            <Button 
              onClick={() => setAddDialog(false)}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (activeTab === 0) {
                  handleAddRate();
                } else {
                  handleAddUserSpecificRate();
                }
              }} 
              variant="contained" 
              color="primary"
            >
              Add
            </Button>
          </DialogActions>
        </Dialog>

        {/* Deactivate Rate Dialog */}
        <Dialog 
          open={deactivateDialog.open} 
          onClose={() => setDeactivateDialog({ open: false, rateId: null })}
        >
          <DialogTitle>Deactivate</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to deactivate this interest rate?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeactivateDialog({ open: false, rateId: null })}>
              Cancel
            </Button>
            <Button 
              onClick={() => {
                if (activeTab === 0) {
                  handleDeactivateRate();
                } else {
                  handleDeactivateUserSpecificRate();
                }
              }} 
              color="default" 
              variant="contained"
            >
              Deactivate
            </Button>
          </DialogActions>
        </Dialog>

        {/* Activate Rate Dialog */}
        <Dialog 
          open={activateDialog.open} 
          onClose={() => setActivateDialog({ open: false, rateId: null })}
        >
          <DialogTitle>Activate</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to activate this interest rate? When activated, other active rates for this user will be automatically deactivated.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setActivateDialog({ open: false, rateId: null })}>
              Cancel
            </Button>
            <Button 
              onClick={() => {
                if (activeTab === 0) {
                  handleActivateRate();
                } else {
                  handleActivateUserSpecificRate();
                }
              }} 
              color="success" 
              variant="contained"
            >
              Activate
            </Button>
          </DialogActions>
        </Dialog>

      </Box>
    </Box>
  );
};

export default InterestRateManagement;

